import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { PaginatedApiV1Response, Promotion } from 'api/types';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import config from '../config';

export type PromotionsParams = {
  searchString?: string;
  page: number;
  itemsPerPage: number;
  country?: string;
  startDate?: string | null;
  endDate?: string | null;
};

interface GetPromotions extends PromotionsParams {
  axios: AxiosInstance;
}
async function getPromotions({
  axios,
  searchString,
  page,
  itemsPerPage,
  country,
  startDate,
  endDate,
}: GetPromotions): Promise<PaginatedApiV1Response<Promotion>> {
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/promotion`, {
    params: {
      searchString: searchString || '',
      page,
      itemsPerPage,
      country,
      start: startDate,
      end: endDate,
    },
  });
  return data;
}

export default function usePromotionsQuery({ searchString, page, itemsPerPage, country, startDate, endDate }: PromotionsParams) {
  const axios = useAxios();
  const toast = useToast();

  const query = useQuery({
    queryKey: getKey({ searchString, page, itemsPerPage, country, startDate, endDate }),
    queryFn: () =>
      getPromotions({
        axios,
        searchString,
        page,
        itemsPerPage,
        country,
        startDate,
        endDate,
      }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, JSON.stringify(query.error as any));
    }
  }, [query.error, query.isError, toast]);

  return query;
}

export function getKey({
  searchString,
  page,
  itemsPerPage,
  country,
  startDate,
  endDate,
}: {
  searchString: string | undefined;
  page: number | undefined;
  itemsPerPage: number | undefined;
  country: string | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
}) {
  return ['Promotions', searchString, page, itemsPerPage, country, startDate, endDate] as const satisfies QueryKey;
}
usePromotionsQuery.getKey = getKey;
